import { useState, useMemo } from 'react'
import { Plus, Info, Trash2, AlertCircle } from 'lucide-react'
import axios from 'axios'
import { RateInput, CurrencyInput } from './RateInput'
import NGNIcon from '../../assets/nigeria.svg'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import {
    useAutoStackingSettings,
    useUpdateAutoStackingSettings,
    useBulkCreateAutoStackingTransactionFees,
    useUpdateAutoStackingTransactionFee,
    useDeleteAutoStackingTransactionFee,
} from '@/hooks/useAutoStacking'
import type { AutoStackingTransactionFee } from '@/types/auto-stacking'
import { validateRanges, rangeErrorMap } from '@/lib/validateRanges'

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        return (
            (error.response?.data as { message?: string })?.message ||
            error.message
        )
    }
    return error instanceof Error
        ? error.message
        : 'An unexpected error occurred'
}

interface LocalFeeRange {
    id?: string
    from: string
    to: string
    fee: string
}

export const AutoStackTab = () => {
    const { data: settings, isLoading, error } = useAutoStackingSettings()
    const updateSettingsMutation = useUpdateAutoStackingSettings()
    const bulkCreateFeesMutation = useBulkCreateAutoStackingTransactionFees()
    const updateFeeMutation = useUpdateAutoStackingTransactionFee()
    const deleteFeeMutation = useDeleteAutoStackingTransactionFee()

    const [feeRangesOverride, setFeeRangesOverride] = useState<
        LocalFeeRange[] | null
    >(null)
    const [dailyInterestRateOverride, setDailyInterestRateOverride] = useState<
        string | null
    >(null)
    const [hasChanges, setHasChanges] = useState(false)

    // Derive effective values: server data as base, user edits on top
    const dailyInterestRate =
        dailyInterestRateOverride ??
        String(settings?.dailyInterestRatePercent ?? '')

    const feeRanges = useMemo<LocalFeeRange[]>(() => {
        if (feeRangesOverride !== null) return feeRangesOverride
        if (settings?.transactionFees && settings.transactionFees.length > 0) {
            return settings.transactionFees.map(
                (f: AutoStackingTransactionFee) => ({
                    id: f.id,
                    from: String(f.fromAmount),
                    to: String(f.toAmount),
                    fee: String(f.feeAmount),
                })
            )
        }
        return [{ from: '', to: '', fee: '' }]
    }, [feeRangesOverride, settings])

    // Validate ranges on every change
    const rangeErrors = useMemo(
        () => rangeErrorMap(validateRanges(feeRanges)),
        [feeRanges]
    )
    const hasRangeErrors = Object.keys(rangeErrors).length > 0

    const markChanged = () => setHasChanges(true)

    const addRange = () => {
        setFeeRangesOverride([...feeRanges, { from: '', to: '', fee: '' }])
        markChanged()
    }

    const removeRange = async (index: number) => {
        const range = feeRanges[index]
        if (range.id) {
            try {
                await deleteFeeMutation.mutateAsync(range.id)
                toast.success('Fee range deleted')
            } catch (err) {
                toast.error('Failed to delete fee range', {
                    description: getErrorMessage(err),
                })
                return
            }
        }
        setFeeRangesOverride(feeRanges.filter((_, i) => i !== index))
    }

    const handleRangeChange = (
        index: number,
        field: keyof Omit<LocalFeeRange, 'id'>,
        value: string
    ) => {
        setFeeRangesOverride(
            feeRanges.map((range, i) =>
                i === index ? { ...range, [field]: value } : range
            )
        )
        markChanged()
    }

    const handleSave = async () => {
        const promises: Promise<unknown>[] = []

        // Save interest rate setting
        promises.push(
            updateSettingsMutation.mutateAsync({
                dailyInterestRatePercent: parseFloat(dailyInterestRate) || 0,
            })
        )

        const newRanges = feeRanges.filter(r => !r.id)
        const existingRanges = feeRanges.filter(r => r.id)

        // Bulk-create new ranges
        if (newRanges.length > 0) {
            promises.push(
                bulkCreateFeesMutation.mutateAsync({
                    fees: newRanges.map(r => ({
                        fromAmount: parseFloat(r.from) || 0,
                        toAmount: parseFloat(r.to) || 0,
                        feeAmount: parseFloat(r.fee) || 0,
                        currency: 'NGN',
                        feeCurrency: 'NGN',
                    })),
                })
            )
        }

        // Update existing ranges
        existingRanges.forEach(r => {
            if (r.id) {
                promises.push(
                    updateFeeMutation.mutateAsync({
                        feeId: r.id,
                        data: {
                            fromAmount: parseFloat(r.from) || 0,
                            toAmount: parseFloat(r.to) || 0,
                            feeAmount: parseFloat(r.fee) || 0,
                            currency: 'NGN',
                            feeCurrency: 'NGN',
                        },
                    })
                )
            }
        })

        try {
            await Promise.all(promises)
            toast.success('Auto stack settings saved successfully')
            setHasChanges(false)
        } catch (err) {
            toast.error('Failed to save auto stack settings', {
                description: getErrorMessage(err),
            })
        }
    }

    const isSaving =
        updateSettingsMutation.isPending ||
        bulkCreateFeesMutation.isPending ||
        updateFeeMutation.isPending

    if (isLoading) {
        return (
            <div className="space-y-8">
                <Skeleton className="h-8 w-full rounded-full" />
                <div className="space-y-4">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="grid gap-4 sm:grid-cols-2">
                            <Skeleton className="h-11 w-full" />
                            <Skeleton className="h-11 w-full" />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-destructive">
                    Failed to load auto stack settings.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {/* Auto Stack Header */}
            <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                <h2 className="text-base font-medium text-white">Auto Stack</h2>
            </div>

            {/* Auto Stack Transaction Fee */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        Auto Stack transaction fee
                    </h3>
                </div>

                <div className="space-y-4">
                    {feeRanges.map((range, index) => (
                        <div key={index} className="space-y-2">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Amount range
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <RateInput
                                            placeholder="From"
                                            value={range.from}
                                            onChange={e =>
                                                handleRangeChange(
                                                    index,
                                                    'from',
                                                    e.target.value
                                                )
                                            }
                                            className={
                                                rangeErrors[index]
                                                    ? 'border-destructive focus-visible:ring-destructive'
                                                    : ''
                                            }
                                        />
                                        <RateInput
                                            placeholder="To"
                                            value={range.to}
                                            onChange={e =>
                                                handleRangeChange(
                                                    index,
                                                    'to',
                                                    e.target.value
                                                )
                                            }
                                            className={
                                                rangeErrors[index]
                                                    ? 'border-destructive focus-visible:ring-destructive'
                                                    : ''
                                            }
                                        />
                                        <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                            <img alt="Nigeria" src={NGNIcon} />
                                            <span className="text-sm font-medium text-foreground">
                                                NGN
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Fee (NGN)
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <RateInput
                                            placeholder="0.00"
                                            value={range.fee}
                                            onChange={e =>
                                                handleRangeChange(
                                                    index,
                                                    'fee',
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                            <img alt="Nigeria" src={NGNIcon} />
                                            <span className="text-sm font-medium text-foreground">
                                                NGN
                                            </span>
                                        </div>
                                        {feeRanges.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    removeRange(index)
                                                }
                                                disabled={
                                                    deleteFeeMutation.isPending
                                                }
                                                className="h-11 w-11 p-0 text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {rangeErrors[index] && (
                                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>{rangeErrors[index]}</span>
                                </div>
                            )}
                        </div>
                    ))}

                    <Button
                        type="button"
                        variant="ghost"
                        onClick={addRange}
                        className="h-auto gap-2 p-0 text-sm text-[#5FA8B4] hover:bg-transparent hover:text-[#5FA8B4]/80"
                    >
                        <Plus className="h-4 w-4" />
                        Add range
                    </Button>
                </div>
            </div>

            {/* Interest Rate */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        Interest rate
                    </h3>
                </div>

                <div className="max-w-md space-y-2">
                    <label className="text-sm font-medium text-foreground">
                        Daily Auto Stack Interest rate
                    </label>
                    <CurrencyInput
                        placeholder="0.0"
                        value={dailyInterestRate}
                        onChange={e => {
                            setDailyInterestRateOverride(e.target.value)
                            markChanged()
                        }}
                        showPercentage
                    />
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                            This amount that will be added to user's account
                        </span>
                    </div>
                </div>
            </div>

            {hasChanges && !hasRangeErrors && (
                <div className="flex justify-start">
                    <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="rounded-full bg-[#17A2B8] px-8 text-white hover:bg-[#1493a7]"
                    >
                        {isSaving ? 'Saving...' : 'Save changes'}
                    </Button>
                </div>
            )}
        </div>
    )
}
